import React from 'react';
import gql from 'graphql-tag';
import { times } from 'lodash';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { CoreHomepageNotifications } from '@reverbdotcom/commons/src/rql';
import I18n from 'i18n-js';
import { OverflowingRow } from '@reverbdotcom/commons';
import { RCGrid } from '@reverbdotcom/cadence/components';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import experiments from '@reverbdotcom/commons/src/experiments';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { HomepageNotificationActivityCard, ActivityCardPlaceholder } from './homepage_notification_activity_card';
import { ChildProps } from 'react-apollo';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

const NOTIFICATION_VIEW_LIMIT = 3;

export function HomepageNotifications(props: IProps) {
  const { data: { me, loading } } = props;
  const user = useUser();
  const notifications = me?.homepageNotifications?.homepageNotifications?.filter(n => !n.dismissed) || [];
  const optedIntoExp = isExperimentEnabled(user, experiments.HOMEPAGE_NOTIFICATIONS_ROW);
  const eligibleForTracking = !loading && !!notifications.length;

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'HomepageNotifications',
    experiments: formatExperimentsForEventAttributes(user, [experiments.HOMEPAGE_NOTIFICATIONS_ROW]),
    notificationTypes: notifications.map(n => n.type).join('|'),
  }, eligibleForTracking);

  if (!optedIntoExp) { return null; }
  const notificationsPresent = !!notifications.length || loading;

  return (
    <AnimatePresence>
      {notificationsPresent &&
        <motion.div
          className="homepage-container bg-white pt-half"
          key="homepage-notifications-container"
          exit={{
            height: 0,
            opacity: 0,
            transition: {
              ease: 'easeOut',
              duration: 0.4,
            },
          }}
        >
          <div className="homepage-notifications-container__component-wrapper">
            <OverflowingRow title={loading ? '' : I18n.t('discovery.homepage.notifications')} >
              <RCGrid singleRowOn="all" disableAndPulse={loading}>
                { loading ?
                  times(NOTIFICATION_VIEW_LIMIT, (index) => {
                    return (
                      <RCGrid.Item
                        key={`homepage-notification-placeholder-${index}`}
                        colSpan={{ desktop: 4, tablet: 6, mobile: 12 }}>
                        <ActivityCardPlaceholder />
                      </RCGrid.Item>
                    );
                  })
                  :
                  <AnimatePresence initial={false}>
                    {
                      notifications.slice(0, NOTIFICATION_VIEW_LIMIT).map((notification) => {
                        return (
                          <RCGrid.Item
                            key={`homepage-notification-${notification.updateId}`}
                            colSpan={{ desktop: 4, tablet: 6, mobile: 12 }}>
                            <HomepageNotificationActivityCard
                              notification={notification}
                            />
                          </RCGrid.Item>
                        );
                      })
                    }
                  </AnimatePresence>
                }
              </RCGrid>
            </OverflowingRow>
          </div>
        </motion.div> }
    </AnimatePresence>
  );
}

export const HOMEPAGE_NOTIFICATIONS_QUERY = gql`
  query Core_HomepageNotifications {
    me {
      uuid
      homepageNotifications(input: {
        limit: 10
      }) {
        homepageNotifications {
          id
          calloutText
          ctaUrl
          deadlineText
          descriptionText
          dismissed
          followText
          imageUrl
          type
          typeText
          updateId
        }
      }
    }
  }
`;

type IProps = ChildProps<Record<string, any>, CoreHomepageNotifications.Query>;

const connect = withGraphql<IProps, CoreHomepageNotifications.Query, CoreHomepageNotifications.Variables>(
  HOMEPAGE_NOTIFICATIONS_QUERY,
  {
    options: () => {
      return {
        ssr: true,
      };
    },
  },
);

export default connect(HomepageNotifications);
